// React and related imports
import React, { useEffect, useState, useRef } from 'react';

// Flowbite components
import { Label, Checkbox } from 'flowbite-react';

// i18 Translation
import { TFunction } from 'i18next';

// Types and Interfaces
import { IAnswer } from '../../common/interfaces/Answer.interface';
import { IOption } from '../../common/interfaces/Option.interface';

// Custom Components
import {
  MultiLanguageList,
  MultiLanguageListItem,
  useMultiLanguageInput,
} from '../Shared/MultiLanguageInput';

interface MultiLangAnswerInputProps {
  t: TFunction<'translation', undefined>;
  answer: IAnswer;
  updateAnswerOptions: (value: IOption[]) => void;
}

export const MultiLangAnswerInput: React.FC<MultiLangAnswerInputProps> = ({
  t,
  answer,
  updateAnswerOptions,
}) => {
  const { selectedLanguage, setSelectedLanguage } = useMultiLanguageInput();
  const isFirstRender = useRef(true);

  // Convert IOption[] to MultiLanguageListItem[]
  const [options, setOptions] = useState<MultiLanguageListItem[]>(
    (answer.dataSources?.[0]?.options || []).map((opt) => ({
      ...opt,
      key: opt.key,
      value: opt.value,
      isCorrect: opt.isCorrect,
    }))
  );

  useEffect(() => {
    // Skip the first render to avoid calling updateAnswerOptions on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Convert MultiLanguageListItem[] back to IOption[] when updating
    const convertedOptions: IOption[] = options.map((opt) => ({
      key: opt.key,
      value: opt.value,
      isCorrect: opt.isCorrect || false,
    }));
    updateAnswerOptions(convertedOptions);
  }, [options, updateAnswerOptions]);

  const toggleCorrectAnswer = (item: MultiLanguageListItem) => {
    setOptions(
      options.map((option) => ({
        ...option,
        isCorrect:
          option.key === item.key ? !option.isCorrect : option.isCorrect,
      }))
    );
  };

  return (
    <MultiLanguageList
      items={options}
      onChange={setOptions}
      selectedLanguage={selectedLanguage}
      onLanguageChange={setSelectedLanguage}
      minItems={2}
      maxItems={6}
      itemLabelKey="content.answerOption"
      addButtonTextKey="content.addAnswerOption"
      languageDropdownLabelKey="content.selectAnswerOptionLanguage"
      placeholderKey="placeholders.option"
      required={true}
      t={t}
      wrapInCard={true}
      renderExtraContent={(item, updateItem) => (
        <>
          <Checkbox
            id={`correct-${item.key}`}
            checked={item.isCorrect || false}
            onChange={() => toggleCorrectAnswer(item)}
            className="mx-2"
          />
          <Label htmlFor={`correct-${item.key}`} className="me-auto">
            {t('content.correctAnswer')}
          </Label>
        </>
      )}
    />
  );
};
